import { useState, useEffect, useCallback, useRef } from "react";

// ─── DESIGN TOKENS ───
const C = {
  bg: "#0a0a0a",
  panel: "#0c0c0c",
  primary: "#33ff00",
  secondary: "#ffb000",
  muted: "#1f521f",
  dim: "#1a7a1a",
  error: "#ff3333",
  border: "#1f521f",
  cyan: "#00e5ff",
};
const GLOW = (c = C.primary, r = 5) => `0 0 ${r}px ${c}88`;
const FONT = "'JetBrains Mono','Fira Code','Courier New',monospace";

// ═══════════════════════════════════════════════════
// ─── STORY: 6 CHAPTERS, McKEE STRUCTURE ───
// ═══════════════════════════════════════════════════

// ─── MOBILE + SAFE AREA CSS ───
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = [
    "html, body, #root { background: #0a0a0a !important; }",
    "html, body { overflow-x: hidden; max-width: 100vw; }",
    "body { padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);",
    "        padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right); }",
    "*, *::before, *::after { box-sizing: border-box; }",
    "div { max-width: 100%; }",
    "@media (max-width: 600px) {",
    "  .f-main { padding: 14px 12px !important; }",
    "  .f-title { font-size: 20px !important; }",
    "  .f-subtitle { font-size: 13px !important; }",
    "  .f-btn { font-size: 13px !important; padding: 12px 18px !important; width: 100% !important; display: block !important; }",
    "  .f-option { font-size: 13px !important; padding: 12px 14px !important; }",
    "  .f-pane { max-width: 100% !important; overflow: hidden !important; }",
    "  .f-pane-body { padding: 10px 12px !important; overflow-x: auto !important; }",
    "  .f-dashboard { grid-template-columns: 1fr !important; }",
    "}"
  ].join("\n");
  document.head.appendChild(style);
}

const CHAPTERS = [
  // ───────────────── ACT I: SURVIVAL ─────────────────
  {
    id: "countdown",
    introLabel: "DAY ONE — THE CLOCK STARTS",
    act: "I",
    actTitle: "SURVIVAL",
    title: "THE COUNTDOWN",
    subtitle: "Day 1 as a funded founder. The clock starts now.",
    terminalIntro: [
            "You're sitting in a coffee shop. Your phone",
      "buzzes: '$500,000 deposited.' You take a",
      "deep breath. This is real now.",
      "",
      "Half a million dollars sounds like a lot.",
      "But every dollar has a purpose, and every",
      "month that passes without revenue means the",
      "balance gets smaller.",
      "",
      "Before you spend a single dollar, you need",
      "to answer one question:",
      "",
      ">> HOW MANY MONTHS DO I HAVE TO FIND",
      "PRODUCT-MARKET FIT? <<",
          ],
    narrative: [
      "> initializing founder_os v0.1 ...",
      "> loading bank_balance ............ $500,000   [OK]",
      "> loading team .................... 1 founder  [OK]",
      "> loading product ................. idea.txt   [OK]",
      "> loading revenue ................. $0         [OK]",
      "",
      "Month 1. You quit your job. You incorporate.",
      "Month 2. You hire your first engineer. Then another.",
      "By Month 3, you have 2 engineers and you handle",
      "product and design yourself.",
      "",
      "Small shared office: $500/mo. Cloud infrastructure: $1,200/mo.",
      "Engineer salaries: $24,000/mo. Software tools: $800/mo.",
      "Miscellaneous: $1,500/mo. Your own salary: $0.",
      "You're keeping costs lean.",
      "",
      "Monthly expenses: $28,000. Monthly revenue: $0.",
      "Your bank balance will shrink every month from here.",
      "",
      "This is the first lesson of being a founder:",
      "your goal is to survive in the short-run,",
      "and build a sustainable business in the long-run.",
      "Knowing exactly how much time you have is step one.",
    ],
    lesson: {
      title: "BURN RATE & RUNWAY",
      why: "Because the #1 reason startups struggle is running out of cash before finding product-market fit. Not bad products, not competition — simply running out of time. Knowing your runway gives you the clarity to make smart, deliberate decisions about every dollar you spend.",
      metrics: [
        {
          name: "BURN RATE",
          def: "How much cash leaves your bank account each month beyond what comes in. If you spend $28K/mo and earn $0, your net burn is $28K/mo. When revenue starts, burn = expenses minus revenue.",
          formula: "net_burn = monthly_expenses - monthly_revenue",
        },
        {
          name: "RUNWAY",
          def: "How many months you can survive at your current burn rate. This is your single most important number as an early-stage founder. It determines every hiring decision, every marketing spend, every pivot timeline.",
          formula: "runway_months = cash_in_bank / net_burn_rate",
        },
      ],
      scenario: {
        intro: "You've been operating for 3 months. Team is hired, office is set up. Monthly expenses: $28K. Revenue: still $0. Bank balance: $500,000.",
        question: "What's your runway?",
        options: [
          { label: "9 months", value: 9, correct: false, feedback: "Too pessimistic on the math (though caution is wise). $500,000 ÷ $28,000 ≈ 18 months. But here's the useful insight: your expenses may grow as you hire more, so effective runway is often shorter than the math suggests." },
          { label: "~18 months", value: 18, correct: true, feedback: "Correct! $500,000 ÷ $28,000 ≈ 17.8 months, roughly 18. That's your planning window. Experienced founders know: plan as if you have 14 months. Things always cost more and take longer. You'll want at least 4-6 months of runway left when you start raising your next round." },
          { label: "24 months", value: 24, correct: false, feedback: "$500K ÷ $28K ≈ 18 months, not 24. It's worth being precise here — overestimating runway can lead to spending too freely early on, leaving you scrambling later." },
          { label: "Doesn't matter — we'll raise more", value: 99, correct: false, feedback: "It's good to be optimistic, but 29% of startup failures cite running out of cash. Fundraising takes 3-6 months and isn't guaranteed. The wisest founders always know their number. Yours is about 18 months — and that knowledge is power." },
        ],
      },
      viz: { type: "runway", cash: 500000, burn: 28000, months: 20 },
    },
    pitfalls: [
      { mistake: "Counting total cash as runway without subtracting upcoming one-time costs (hiring, equipment, deposits)", fix: "Build a simple monthly cash flow sheet that includes one-time expenses. Your real runway is always shorter than cash ÷ burn." },
      { mistake: "Ignoring that burn rate INCREASES as you hire — your Month 1 burn is not your Month 6 burn", fix: "Model your burn rate 6 months out with planned hires included. Use the projected average, not today's number." },
      { mistake: "Assuming you can raise more money anytime — fundraising takes 3-6 months and isn't guaranteed", fix: "Start fundraising when you have 6+ months of runway left. If you wait until month 3, you're negotiating from desperation." },
    ],
    takeaway: "Your runway is your planning horizon. Know the exact number of months you have — it's the foundation for every decision. Experienced founders plan as if they have 6 months less than the math says.",
  },
  {
    id: "contact",
    introLabel: "MONTH 4 — LAUNCH DAY",
    act: "I",
    actTitle: "SURVIVAL",
    title: "FIRST CONTACT",
    subtitle: "You built it. Will they come? Will they stay?",
    terminalIntro: [
            "Month 4. You're about to press 'deploy.'",
      "Four months of building. Sleepless nights.",
      "Heated arguments about features.",
      "",
      "Your designer says 'it's not ready.' Your",
      "engineer says 'ship it.' Your investor says",
      "'I need to see traction by month 6.'",
      "",
      "You ship it. You post everywhere. You email",
      "everyone you know. Then you watch the",
      "analytics dashboard and wait.",
      "",
      "5,000 people show up on day one.",
      "By day seven, almost all of them are gone.",
      "",
      ">> WHAT HAPPENED? WHERE DID THEY GO?",
      "AND HOW DO I MEASURE WHAT WENT WRONG? <<",
          ],
    narrative: [
      "> deploy --production ............ [OK]",
      "> status: LIVE",
      "> monitoring incoming connections...",
      "> day_01: 5,000 visitors ......... [SPIKE]",
      "> day_01: 800 signups ............ [GOOD?]",
      "> day_01: 340 completed onboarding [HMMMM]",
      "> day_07: 180 came back .......... [CONCERN]",
      "",
      "Let's slow down. 5,000 people visited.",
      "800 gave you their email. That's 16% — not bad.",
      "",
      "But only 340 actually USED your product.",
      "That means 460 people signed up but didn't fully engage.",
      "They never reached the moment where your product clicks.",
      "",
      "And of the 340 who did try it, only 180 returned",
      "a week later. There's room to improve here.",
      "",
      "A startup is a funnel. People naturally drop off at",
      "every stage. The key is understanding WHERE and WHY.",
      "Think of it like this:",
      "",
      "[[FUNNEL_MINI]]",
      "",
      "Every stage loses people. This framework is called",
      "AARRR — the Pirate Metrics (because it sounds",
      "like a pirate). Fun name, but incredibly powerful.",
      "It tells you exactly WHERE your funnel needs work.",
    ],
    lesson: {
      title: "AARRR — THE PIRATE METRICS",
      why: "Because you can't improve what you can't measure. AARRR gives you the diagnostic framework to find WHERE your funnel needs attention. Without it, you're guessing. With it, you know exactly which stage to focus on first.",
      metrics: [
        {
          name: "ACQUISITION",
          def: "How do people discover you exist? Website visits, app downloads, ad clicks. This is the top of your funnel — the widest part. If nobody shows up, nothing else matters.",
          formula: "acquisition = total_new_visitors_or_downloads",
        },
        {
          name: "ACTIVATION",
          def: "Do first-time users have a GOOD first experience? This is the 'aha moment' — when someone goes from 'what is this?' to 'oh, I get it.' For Dropbox, it was saving your first file. For Twitter, following 30 people. What's yours?",
          formula: "activation_rate = users_who_hit_aha_moment / total_signups",
        },
        {
          name: "RETENTION",
          def: "Do users come back after day one? This is THE most important pirate metric. If people try your product and don't return, you have a product problem — not a marketing problem. Fix this before spending on growth.",
          formula: "D7_retention = users_active_on_day_7 / users_from_day_0",
        },
        {
          name: "REVENUE",
          def: "Can you turn usage into money? This doesn't always come first — sometimes you need to prove retention before monetizing. But eventually, free users need to become paying customers.",
          formula: "conversion_rate = paying_users / active_users",
        },
        {
          name: "REFERRAL",
          def: "Do users love it enough to tell others? This is the holy grail. If each user brings in >1 new user (K-factor > 1), you have viral growth and your acquisition cost drops toward zero.",
          formula: "k_factor = avg_invites_per_user × invite_conversion_rate",
        },
      ],
      scenario: {
        intro: "Launch day results: 5,000 visitors → 800 signups → 340 completed onboarding → 180 active after week 1 → 27 eventually paid → 12 referred a friend. Your ACTIVATION rate is the % of signups who completed onboarding (the 'aha moment').",
        question: "What is your activation rate?",
        options: [
          { label: "16% (800/5000)", value: 16, correct: false, feedback: "That's your signup rate — the conversion from visitor to signup. That's Acquisition, not Activation. Activation = people who hit the 'aha moment' / signups = 340/800 = 42.5%." },
          { label: "42.5% (340/800)", value: 42.5, correct: true, feedback: "Correct. 340 activated / 800 signups = 42.5%. The industry average is 20-40%, so you're slightly above average. But look at it this way: 460 people gave you their email and never experienced your product properly. That's 460 wasted opportunities. What if your onboarding was better?" },
          { label: "52.9% (180/340)", value: 52.9, correct: false, feedback: "That's actually your Week 1 retention rate — of those who activated, how many came back. Important metric, but different question. Activation = completed onboarding / signups = 340/800 = 42.5%." },
          { label: "3.6% (180/5000)", value: 3.6, correct: false, feedback: "That's your full-funnel conversion (visitors to retained users). Useful to know, but Activation specifically measures signups → first meaningful experience = 340/800 = 42.5%." },
        ],
      },
      viz: {
        type: "funnel",
        data: [
          { stage: "VISITORS", value: 5000 },
          { stage: "SIGNUPS", value: 800 },
          { stage: "ACTIVATED", value: 340 },
          { stage: "RETAINED W1", value: 180 },
          { stage: "PAYING", value: 27 },
          { stage: "REFERRED", value: 12 },
        ],
      },
    },
    pitfalls: [
      { mistake: "Celebrating signup numbers without tracking activation — 800 signups means nothing if only 340 actually use the product", fix: "Define your 'aha moment' clearly (e.g., 'completed first project') and track the % of signups who reach it." },
      { mistake: "Spending on acquisition (ads, marketing) before fixing activation and retention — you're filling a leaky bucket", fix: "Work the funnel bottom-up: fix retention first, then activation, then pour money into acquisition." },
      { mistake: "Treating all AARRR stages as equally important — they're not. Retention is almost always the highest-leverage fix", fix: "If D7 retention is below 20%, stop everything else and fix the product. Nothing else matters until people come back." },
    ],
    takeaway: "Don't just count users — measure WHERE they drop off. Fix the biggest leak in your funnel first. Activation is almost always the most underinvested stage.",
  },

  // ───────────────── ACT II: THE STRUGGLE ─────────────────
  {
    id: "bucket",
    introLabel: "MONTH 8 — THE LEAKY BUCKET",
    act: "II",
    actTitle: "THE STRUGGLE",
    title: "THE LEAKY BUCKET",
    subtitle: "You're growing. But you're not. The math doesn't lie.",
    terminalIntro: [
            "Month 8. You're in your weekly team standup.",
      "Marketing reports a record month: 2,000 new",
      "signups. The team is energized.",
      "",
      "Then you open the dashboard. Total active",
      "users: 1,024. Last month? 1,018.",
      "",
      "You added 2,000 people and gained... 6?",
      "The room goes quiet.",
      "",
      "That night, you get a one-line email from",
      "your board member:",
      "",
      "'Fix retention before you spend another",
      "dollar on growth.'",
      "",
      ">> HE'S RIGHT. HERE'S WHY. <<",
          ],
    narrative: [
      "> alert: ANOMALY DETECTED",
      "> new_signups_month_8: 2,000",
      "> net_active_user_change: +6",
      "> running churn_analysis ............ [DONE]",
      "> monthly_churn_rate: 12.4% ......... [CRITICAL]",
      "",
      "Here's what's happening behind the numbers:",
      "",
      "You started Month 8 with 1,018 active users.",
      "During the month, 126 of them cancelled or went inactive.",
      "That's a 12.4% monthly churn rate.",
      "",
      "You added 2,000 new signups. After activation drop-off,",
      "about 132 became real active users.",
      "132 in, 126 out. Net gain: 6.",
      "",
      "You're putting in effort, but the results aren't showing.",
      "All that growth is being offset by users leaving.",
      "",
      "And here's the critical insight: Churn COMPOUNDS.",
      "It's not just 12.4% of this month's users.",
      "It's 12.4% EVERY month, stacking on itself.",
      "Understanding this math changes how you prioritize.",
    ],
    lesson: {
      title: "RETENTION & CHURN",
      why: "Because churn is the quiet challenge that catches many founders off guard. A 5% monthly churn rate sounds small — but it means losing nearly half your customers every year. No amount of marketing can outpace a leaky bucket. Retention is the foundation everything else is built on.",
      metrics: [
        {
          name: "CHURN RATE",
          def: "The percentage of customers who stop using your product each month. It sounds small — 'just' 5% or 10% — but it compounds significantly over time. At 10% monthly churn, you lose 72% of your customers in a year. At 5%, you lose 46%. Understanding this compounding effect is key.",
          formula: "monthly_churn = customers_lost_this_month / customers_at_start_of_month",
        },
        {
          name: "NET DOLLAR RETENTION (NDR)",
          def: "Revenue from existing customers compared to last period, including upsells and downgrades. If NDR > 100%, your existing customers are spending MORE over time — you grow even without new customers. The best SaaS companies have NDR of 120%+.",
          formula: "NDR = (start_MRR + expansion - contraction - churned_MRR) / start_MRR",
        },
      ],
      scenario: {
        intro: "Your churn rate is 12.4% per month. You start with a cohort of 1,000 users today. No new users are added — you're tracking this original group. Churn compounds: each month, you lose 12.4% of whoever remains.",
        question: "After 12 months, how many of the original 1,000 are still with you?",
        options: [
          { label: "~850 users remain", value: 850, correct: false, feedback: "That's only one month of churn (1000 - 124 = 876). But churn COMPOUNDS. Month 2: 876 × 0.876 = 767. Month 3: 672. It keeps shrinking. After 12 months: 1000 × (0.876)^12 ≈ 196." },
          { label: "~500 users remain", value: 500, correct: false, feedback: "It's actually more than that. 12.4% monthly churn compounds significantly. 1000 × (1 - 0.124)^12 = 196. You'd lose over 80% in a single year. This is why your board member is so focused on retention." },
          { label: "~196 users remain", value: 196, correct: true, feedback: "Correct. 1000 × (0.876)^12 ≈ 196. Over 80% of your users are gone in one year. To maintain your current user count, you'd need to acquire 5x your base every year. That's a treadmill, not growth. The good news: fixing retention is one of the highest-leverage things you can do. Even reducing churn from 12.4% to 5% changes the picture dramatically." },
          { label: "~50 users remain", value: 50, correct: false, feedback: "Not quite that severe — 1000 × (0.876)^12 ≈ 196. But your instinct that it's a serious problem is spot on. 80% user loss in a year is something that needs immediate attention." },
        ],
      },
      viz: { type: "churn", startUsers: 1000, churnRate: 0.124, months: 12 },
    },
    pitfalls: [
      { mistake: "Thinking 5% monthly churn is 'small' — it compounds to 46% annual loss. 10% monthly = 72% gone in a year", fix: "Always annualize your churn to feel the real impact. Monthly churn × 12 is NOT the right formula — use (1 - churn)^12." },
      { mistake: "Measuring churn on total users instead of by cohort — this hides whether things are getting better or worse", fix: "Track cohort retention curves: how does the January cohort behave vs. the March cohort? Improving curves = real progress." },
      { mistake: "Confusing user churn with revenue churn — losing 10 free users is very different from losing 2 enterprise customers", fix: "Track both logo churn (customers lost) and revenue churn (MRR lost) separately. Revenue churn is what hits your bank account." },
    ],
    takeaway: "Focus on retention before investing in growth. Churn compounds — even 'small' monthly churn erodes your user base faster than you'd expect. A strong foundation of retained users makes everything else easier.",
  },
  {
    id: "price",
    introLabel: "MONTH 12 — THE REVENUE PROBLEM",
    act: "II",
    actTitle: "THE STRUGGLE",
    title: "THE PRICE OF FREE",
    subtitle: "Users love your product. Now it's time to build a business around it.",
    terminalIntro: [
            "Month 12. You did it. You fixed retention.",
      "Churn dropped from 12.4% to 4.1%. Your",
      "users love the product. 2,400 active users.",
      "",
      "You call your co-founder to celebrate.",
      "He says: 'Check the bank account.'",
      "",
      "$164,000 remaining. About 6 months of runway.",
      "",
      "2,400 happy users. Zero dollars in revenue.",
      "You've built something people want.",
      "Now you need to build something people will",
      "PAY for — and soon.",
      "",
      ">> YOUR INVESTORS DON'T CARE ABOUT 'REVENUE.'",
      "THEY CARE ABOUT RECURRING REVENUE.",
      "HERE'S WHY THE DISTINCTION MATTERS. <<",
          ],
    narrative: [
      "> revenue_engine --initialize",
      "> pricing_tiers: free | pro($19/mo) | enterprise($79/mo)",
      "> deploying paywall ............ [OK]",
      "> first_payment_received ........ [!!] $19.00",
      "",
      "That first $19 hits different.",
      "Someone, somewhere, decided your product is worth",
      "actual money. Not your mom. Not your friend. A stranger.",
      "",
      "Month 12: 120 users upgrade to Pro. That's $2,280/mo.",
      "Month 13: 180 Pro + 3 Enterprise = $3,657/mo.",
      "Month 14: 240 Pro + 8 Enterprise = $5,192/mo.",
      "",
      "But when your investor asks 'what's your revenue?',",
      "he doesn't mean the $2,000 consulting gig you did.",
      "He doesn't mean the one-time setup fee.",
      "",
      "He means: what money comes in EVERY month,",
      "predictably, without you having to re-sell anything?",
      "That's MRR. And ARR is just MRR times twelve.",
      "",
      "These two numbers will define your company's valuation.",
    ],
    lesson: {
      title: "MRR & ARR",
      why: "Because investors value predictability above all else. A company earning $100K/year from one-time projects is worth far less than one earning $100K/year from subscriptions. MRR and ARR tell investors: 'This revenue will be here next month, and the month after that.' It's the engine of SaaS valuation.",
      metrics: [
        {
          name: "MRR (Monthly Recurring Revenue)",
          def: "The sum of all predictable, recurring subscription revenue per month. NOT one-time payments. NOT services. NOT setup fees. Only the money that shows up automatically, month after month, from active subscriptions.",
          formula: "MRR = Σ (monthly_price × active_subscribers_on_that_plan)",
        },
        {
          name: "ARR (Annual Recurring Revenue)",
          def: "MRR × 12. This is the number that determines your valuation. Early-stage SaaS companies are typically valued at 10-20x ARR. So if your ARR is $62K, your company could be worth $620K-$1.2M on paper.",
          formula: "ARR = MRR × 12",
        },
      ],
      scenario: {
        intro: "Month 14: You have 240 Pro subscribers ($19/mo each) and 8 Enterprise customers ($79/mo each). You also closed a $2,000 one-time consulting deal with a company who wanted custom integration help.",
        question: "What is your MRR?",
        options: [
          { label: "$7,192 (all revenue this month)", value: 7192, correct: false, feedback: "You're including the $2K consulting deal. That's one-time revenue — it won't repeat next month. MRR is ONLY recurring: (240 × $19) + (8 × $79) = $4,560 + $632 = $5,192." },
          { label: "$5,192 (subscriptions only)", value: 5192, correct: true, feedback: "Correct. MRR = (240 × $19) + (8 × $79) = $4,560 + $632 = $5,192. The $2K consulting deal is excluded — it's not recurring. Your ARR = $5,192 × 12 = $62,304. At a 15x multiple, that's a ~$935K valuation. You're starting to build real enterprise value." },
          { label: "$4,560 (Pro subscribers only)", value: 4560, correct: false, feedback: "Don't forget Enterprise! Those 8 customers at $79/mo add $632. MRR = $4,560 + $632 = $5,192. Enterprise revenue is often the fastest-growing segment." },
          { label: "$62,304 (annualized)", value: 62304, correct: false, feedback: "That's your ARR (MRR × 12), not your MRR. Important distinction: MRR = $5,192/mo. ARR = $62,304/yr. Investors use both, but MRR is what you track monthly." },
        ],
      },
      viz: {
        type: "mrr",
        data: [
          { month: "M12", pro: 2280, ent: 0 },
          { month: "M13", pro: 3420, ent: 237 },
          { month: "M14", pro: 4560, ent: 632 },
          { month: "M15", pro: 5510, ent: 1027 },
          { month: "M16", pro: 6460, ent: 1422 },
          { month: "M17", pro: 7220, ent: 1896 },
        ],
      },
    },
    pitfalls: [
      { mistake: "Including one-time payments (consulting, setup fees, pilot projects) in MRR — this inflates your recurring metric", fix: "MRR is ONLY subscription revenue that repeats automatically. One-time revenue goes in a separate line item." },
      { mistake: "Counting annual contracts as full ARR upfront instead of spreading as MRR — a $12K annual contract is $1K MRR, not $12K MRR", fix: "Normalize everything to monthly: annual contracts ÷ 12 = MRR contribution. Then ARR = MRR × 12." },
      { mistake: "Ignoring MRR components: new MRR, expansion MRR, contraction MRR, churned MRR — the net number hides the story", fix: "Break MRR into its 4 components every month. Growing new MRR but also growing churned MRR = a warning sign." },
    ],
    takeaway: "Investors don't value total revenue — they value RECURRING revenue. MRR is the heartbeat of your business; ARR is the number that sets your valuation. One-time deals are nice, but subscriptions build empires.",
  },

  // ───────────────── ACT II (cont): GROWTH TRAP ─────────────────
  {
    id: "trap",
    introLabel: "MONTH 18 — THE GROWTH TRAP",
    act: "II",
    actTitle: "THE STRUGGLE",
    title: "THE GROWTH TRAP",
    subtitle: "Spend money to make money. But how much is too much?",
    terminalIntro: [
            "Month 18. Revenue is real. ARR just crossed",
      "$100K. Your investor meets you for coffee and",
      "says: 'It's time to pour fuel on the fire.",
      "Allocate $8,000/month for marketing. Grow.'",
      "",
      "You split it across 4 channels: Google Ads,",
      "content marketing, outbound sales, referrals.",
      "",
      "After 2 months, the results come in.",
      "Some channels are working well.",
      "One channel is underperforming.",
      "",
      "You're acquiring customers for $60 on one",
      "channel, and $680 on another. But which",
      "customers are actually WORTH the spend?",
      "",
      ">> YOU NEED TO KNOW: IS EACH CUSTOMER WORTH",
      "MORE THAN IT COSTS TO GET THEM? THIS IS",
      "THE MOST IMPORTANT EQUATION IN STARTUPS.<<",
          ],
    narrative: [
      "> growth_engine --status",
      "> budget: $8,000/mo allocated across 4 channels",
      "> analyzing cost_per_acquisition ...... [DONE]",
      "",
      ">  CHANNEL        SPEND     NEW CUST   COST/CUST",
      ">  ─────────────  ────────  ─────────  ─────────",
      ">  Organic/SEO    $1,200    12         $100",
      ">  Paid Search    $3,400    5          $680",
      ">  Content Mkt    $2,000    8          $250",
      ">  Referral       $600      10         $60",
      ">  Outbound       $800      1          $800",
      "",
      "The raw numbers are clear: Referral is cheapest at $60",
      "per customer. Paid Search and Outbound are expensive.",
      "",
      "But WAIT. Cheap doesn't mean profitable.",
      "A customer who costs $60 but leaves in 2 months",
      "might be WORSE than one who costs $680 but stays 3 years.",
      "",
      "This is why you need TWO numbers, not one:",
      "What does it COST to get a customer? (CAC)",
      "What is that customer WORTH over their lifetime? (LTV)",
      "And the ratio between them tells you everything.",
    ],
    lesson: {
      title: "CAC, LTV & THE GOLDEN RATIO",
      why: "Because growth without unit economics is just expensive losing. If you spend more to acquire a customer than that customer will ever pay you, every new customer takes you further from sustainability. The LTV:CAC ratio is the equation that separates companies that scale profitably from companies that scale into trouble.",
      metrics: [
        {
          name: "CAC (Customer Acquisition Cost)",
          def: "Total sales and marketing spend divided by the number of new customers acquired in that period. This tells you the 'price tag' of each new customer. Track it per-channel — your blended CAC hides which channels are working and which aren't.",
          formula: "CAC = total_sales_and_marketing_spend / new_customers_acquired",
        },
        {
          name: "LTV (Customer Lifetime Value)",
          def: "The total revenue a customer generates over their entire relationship with you. Depends on three things: how much they pay monthly (ARPU), how long they stay (1/churn), and your profit margin. A customer paying $22/mo who stays 20 months at 80% margin = $352 LTV.",
          formula: "LTV = ARPU × avg_customer_lifespan × gross_margin",
        },
        {
          name: "LTV:CAC RATIO",
          def: "The golden ratio of startup economics. Below 1:1 = you're losing money on every customer. 3:1 = healthy (for every $1 spent on acquisition, you get $3 back). Above 5:1 = you're probably under-investing in growth and leaving money on the table.",
          formula: "target: LTV/CAC ≥ 3.0  |  payback < 12 months",
        },
      ],
      scenario: {
        intro: "Your blended CAC across all channels is $220. Average customer pays $22/mo (blended across Pro and Enterprise), stays about 20 months, and your gross margin is 80%. LTV = $22 × 20 × 0.80 = $352. Your CAC is $220. Your LTV is $352.",
        question: "Your LTV:CAC ratio is $352 / $220 = 1.6:1. Is that good enough?",
        options: [
          { label: "Yes — we're making money on each customer", value: 1, correct: false, feedback: "You're technically profitable per customer, but 1.6:1 is very thin. After accounting for overhead, support costs, and the time value of money, there's almost no margin left. The benchmark is 3:1 — you have an opportunity to nearly double this ratio by optimizing your channels." },
          { label: "No — we need at least 3:1 to be healthy", value: 2, correct: true, feedback: "Exactly right. 1.6:1 means for every $1 spent, you get $1.60 back — but that barely covers overhead and support costs. At 3:1, you have real margin to reinvest. Your levers: (1) Cut CAC — double down on Referral ($60 CAC) and cut Outbound ($800). (2) Increase LTV — reduce churn to extend lifespan, or upsell. (3) Both." },
          { label: "Just double our prices", value: 3, correct: false, feedback: "Doubling prices would push LTV to $704 and the ratio to 3.2:1 — great on paper. But price increases can spike churn. A 2x price hike with 25% more churn could make things worse. Better to fix the ratio with multiple levers: improve CAC AND LTV together." },
          { label: "This is fine for early-stage", value: 4, correct: false, feedback: "Early-stage CAC can be high, but 1.6:1 leaves very little room for variability. If churn ticks up or CAC increases slightly, you could find yourself below 1:1. Experienced founders treat 3:1 as the minimum healthy ratio before scaling spend." },
        ],
      },
      viz: {
        type: "ltvcac",
        channels: [
          { name: "ORGANIC/SEO", cac: 100, ltv: 420 },
          { name: "PAID SEARCH", cac: 680, ltv: 280 },
          { name: "CONTENT MKT", cac: 250, ltv: 380 },
          { name: "REFERRAL", cac: 60, ltv: 510 },
          { name: "OUTBOUND", cac: 800, ltv: 340 },
        ],
      },
    },
    pitfalls: [
      { mistake: "Calculating LTV using total revenue instead of GROSS PROFIT — if your margin is 60%, a $22/mo customer's real LTV contribution is $13.20/mo, not $22", fix: "Always multiply by gross margin: LTV = ARPU × lifespan × gross_margin. Without margin, you're overstating the value of every customer." },
      { mistake: "Using blended CAC across all channels — this hides that Referral ($60) and Outbound ($800) are wildly different", fix: "Track CAC per channel. Double down on channels with LTV:CAC > 3:1. Cut or fix channels below 1:1." },
      { mistake: "Ignoring payback period — even if LTV:CAC is 3:1, if it takes 24 months to pay back, you need a lot of cash upfront to grow", fix: "Track CAC payback months = CAC ÷ (ARPU × gross_margin). Healthy SaaS: under 12 months. Over 18 months = cash flow danger." },
    ],
    takeaway: "Understand the value of each customer before scaling spend. Aim for LTV:CAC of at least 3:1. Track it per channel — your blended number can mask which channels deserve more investment and which need rethinking.",
  },

  // ───────────────── ACT III: THE RECKONING ─────────────────
  {
    id: "board",
    introLabel: "MONTH 24 — THE BOARD MEETING",
    act: "III",
    actTitle: "THE MILESTONE",
    title: "THE BOARD MEETING",
    subtitle: "Month 24. Your numbers tell a story. Make it a compelling one.",
    terminalIntro: [
            "Month 24. You walk into the boardroom.",
      "Your investor. A whiteboard. Your laptop.",
      "",
      "In the last 6 months, you made hard choices:",
      "- Killed Paid Search entirely (LTV:CAC 0.41:1)",
      "- Doubled referral budget (LTV:CAC 8.5:1)",
      "- Rebuilt onboarding (activation: 42% → 67%)",
      "- Fixed the worst churn triggers (12.4% → 3.1%)",
      "",
      "Your lead investor leans forward and says:",
      "'Show me the numbers.'",
      "",
      "This is the moment. Every metric you've",
      "learned leads to this. Not just the numbers —",
      "but the STORY they tell.",
      "",
      ">> A GOOD STARTUP ISN'T A SPREADSHEET.",
      "IT'S A NARRATIVE, BACKED BY DATA. <<",
          ],
    narrative: [
      "> presentation_mode --enable",
      "> loading 24_months_of_data ............ [OK]",
      "> generating investor_report ........... [OK]",
      "> confidence_level: HIGH",
      "",
      "24 months ago, you had $500K and an idea.",
      "Today, you have a machine.",
      "",
      "You learned that RUNWAY is your planning horizon.",
      "You learned that AARRR shows where the funnel breaks.",
      "You learned that CHURN compounds quietly against you.",
      "You learned that MRR is the engine investors value.",
      "You learned that LTV:CAC is the equation that proves",
      "your business model works.",
      "",
      "Every number on this dashboard is a chapter",
      "of a story you lived. And now, the question",
      "your investor is really asking isn't 'what are",
      "your metrics?' It's this:",
      "",
      "'If I invest more, will this machine scale?'",
      "",
      "Let's look at your answer.",
    ],
    lesson: {
      title: "THE FULL PICTURE — YOUR METRICS, YOUR STORY",
      why: "Because no metric exists in isolation. ARR means nothing without knowing your churn. Low CAC means nothing if LTV is lower. A great activation rate means nothing if nobody shows up. The power is in how the metrics CONNECT — and the story they tell about where your company is going.",
      metrics: [],
      scenario: null,
      viz: {
        type: "dashboard",
        data: {
          arr: "$240K",
          mrr: "$20K",
          burn: "$32K/mo",
          runway: "14 months",
          ltv: "$580",
          cac: "$135",
          ratio: "4.3:1",
          churn: "3.1%",
          ndr: "112%",
          activation: "67%",
          customers: 680,
        },
      },
    },
    pitfalls: [
      { mistake: "Presenting metrics without connecting them — ARR alone doesn't impress if churn is eating growth alive", fix: "Tell the STORY: 'Our churn dropped from 12% to 3%, which means our LTV doubled, which means our LTV:CAC went from 1.6 to 4.3.'" },
      { mistake: "Cherry-picking your best metrics and hiding the weak ones — investors will find them, and you'll lose credibility", fix: "Lead with your strengths, but address weaknesses proactively: 'Our CAC is still high on paid search — here's our plan to fix it.'" },
      { mistake: "Optimizing metrics in isolation instead of as a system — cutting CAC by lowering quality leads worsens retention and LTV", fix: "Always check second-order effects. When you change one metric, ask: what happens to the others? The dashboard is a connected system." },
    ],
    takeaway: "Metrics don't exist in isolation. The real power is in the story they tell together: how you survive (runway), how you grow (AARRR), how you keep (retention), how you earn (MRR), and how you scale (LTV:CAC).",
  },
];

// ═══════════════════════════════════════════════════
// ─── REUSABLE COMPONENTS ───
// ═══════════════════════════════════════════════════

function Scanlines() {
  return <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, background: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.06) 2px,rgba(0,0,0,0.06) 4px)" }} />;
}

function Cursor({ color = C.primary }) {
  const [on, setOn] = useState(true);
  useEffect(() => { const i = setInterval(() => setOn(v => !v), 530); return () => clearInterval(i); }, []);
  return <span style={{ color, opacity: on ? 1 : 0 }}>█</span>;
}

function TypedLines({ lines, speed = 25, lineDelay = 60, onDone, color = C.primary }) {
  const [displayed, setDisplayed] = useState([]);
  const [curLine, setCurLine] = useState(0);
  const [curChar, setCurChar] = useState(0);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (curLine >= lines.length) { setDone(true); onDone?.(); return; }
    const line = lines[curLine];
    if (line === "" || line === "[[FUNNEL_MINI]]" || curChar >= line.length) {
      const t = setTimeout(() => { setDisplayed(d => [...d, line]); setCurLine(l => l + 1); setCurChar(0); }, line === "" ? lineDelay / 2 : lineDelay);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCurChar(c => c + 1), line.startsWith(">") ? 10 : speed);
    return () => clearTimeout(t);
  }, [curLine, curChar, lines, speed, lineDelay]);

  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [displayed, curChar]);

  const FUNNEL_DATA = [
    { label: "VISITORS ", value: 5000, max: 5000 },
    { label: "SIGNUPS  ", value: 800,  max: 5000 },
    { label: "ACTIVATED", value: 340,  max: 5000 },
    { label: "RETAINED ", value: 180,  max: 5000 },
    { label: "PAYING   ", value: 27,   max: 5000 },
    { label: "REFERRED ", value: 12,   max: 5000 },
  ];
  const FUNNEL_COLORS = [C.primary, "#2bcc00", C.secondary, "#e09500", "#ff7733", C.cyan];

  const isCmd = (l) => l.startsWith(">");
  const isHi = (l) => l.startsWith(">>");
  const isMini = (l) => l === "[[FUNNEL_MINI]]";

  const renderLine = (l, i) => {
    if (isMini(l)) {
      return (
        <div key={i} style={{ margin: "6px 0" }}>
          {FUNNEL_DATA.map((d, j) => {
            const pct = d.value / d.max;
            const col = FUNNEL_COLORS[j];
            return (
              <div key={j} style={{ display: "flex", alignItems: "center", marginBottom: 3, overflow: "hidden" }}>
                <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 68, flexShrink: 0 }}>{d.label}</span>
                <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
                  <div style={{ width: `${pct * 100}%`, height: 10, background: col, boxShadow: GLOW(col, 2), flexShrink: 0 }} />
                  <div style={{ flex: 1, height: 10, background: `${col}22` }} />
                </div>
                <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 44, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>{d.value.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div key={i} style={{
        color: isHi(l) ? C.secondary : isCmd(l) ? C.dim : color,
        textShadow: isHi(l) ? GLOW(C.secondary, 4) : isCmd(l) ? GLOW(C.primary, 3) : "none",
        whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word",
        minHeight: l === "" ? 10 : undefined, maxWidth: "100%",
        borderRight: isHi(l) ? `3px solid ${C.secondary}` : "none",
        paddingRight: isHi(l) ? 8 : 0,
      }}>{l}</div>
    );
  };

  return (
    <div ref={ref} style={{ fontFamily: FONT, fontSize: 15, lineHeight: 1.9, maxHeight: 400, overflowY: "auto", overflowX: "hidden", wordBreak: "break-word", maxWidth: "100%" }}>
      {displayed.map((l, i) => renderLine(l, i))}
      {!done && curLine < lines.length && !isMini(lines[curLine]) && (
        <div style={{
          color: isHi(lines[curLine]) ? C.secondary : isCmd(lines[curLine]) ? C.dim : color,
          textShadow: isHi(lines[curLine]) ? GLOW(C.secondary, 4) : isCmd(lines[curLine]) ? GLOW(C.primary, 3) : "none",
          wordBreak: "break-word",
        }}>
          {lines[curLine].slice(0, curChar)}<Cursor />
        </div>
      )}
    </div>
  );
}

function Pane({ title, children, accent = C.primary, status, style = {} }) {
  return (
    <div className="f-pane" style={{ border: `1px solid ${C.border}`, background: C.panel, maxWidth: "100%", overflow: "hidden", ...style }}>
      <div style={{ background: C.border, padding: "5px 12px", fontFamily: FONT, fontSize: 13, color: accent, letterSpacing: 1.5, textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
        <span>{title}</span>
        {status && <span style={{ color: status === "OK" || status === "LIVE" ? C.primary : status === "WARN" ? C.secondary : C.error, fontSize: 10 }}>[{status}]</span>}
      </div>
      <div className="f-pane-body" style={{ padding: "14px 14px", overflowX: "hidden" }}>{children}</div>
    </div>
  );
}

// ─── VISUALIZATIONS ───

function RunwayViz({ cash, burn, months }) {
  const data = []; let rem = cash;
  for (let i = 0; i <= months; i++) { data.push(rem); rem = Math.max(0, rem - burn); }
  return (
    <Pane title="RUNWAY SIMULATION" status="LIVE" accent={C.secondary}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 10, letterSpacing: 1 }}>{">"} CASH REMAINING BY MONTH ($K) — ${(burn/1000).toFixed(0)}K/MO BURN</div>
      {data.map((v, i) => {
        const pct = v / cash;
        const col = v > cash * 0.5 ? C.primary : v > cash * 0.2 ? C.secondary : C.error;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 2, overflow: "hidden" }}>
            <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 28, flexShrink: 0 }}>M{String(i).padStart(2, "0")}</span>
            <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
              <div style={{ width: `${pct * 100}%`, height: 9, background: col, boxShadow: GLOW(col, 2), flexShrink: 0 }} />
              <div style={{ flex: 1, height: 9, background: `${col}22` }} />
            </div>
            <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 44, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>${(v / 1000).toFixed(0)}K</span>
            {v === 0 && <span style={{ fontFamily: FONT, fontSize: 10, color: C.error, fontWeight: "bold", paddingLeft: 4, flexShrink: 1 }}>OUT</span>}
          </div>
        );
      })}
    </Pane>
  );
}

function FunnelViz({ data }) {
  const max = data[0].value;
  const BAR_W = typeof window !== "undefined" && window.innerWidth <= 600 ? 110 : 160;
  return (
    <Pane title="AARRR PIRATE FUNNEL" status="OK" accent={C.secondary}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 10 }}>{">"} LAUNCH DAY — FULL CONVERSION FUNNEL</div>
      {data.map((d, i) => {
        const pct = d.value / max;
        const colors = [C.primary, "#2bcc00", C.secondary, "#e09500", "#ff7733", C.cyan];
        const col = colors[i];
        const conv = i > 0 ? `${((d.value / data[i - 1].value) * 100).toFixed(1)}%` : null;
        return (
          <div key={i} style={{ marginBottom: 5 }}>
            <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
              <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 64, flexShrink: 0 }}>{d.stage}</span>
              <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
                <div style={{ width: `${pct * 100}%`, height: 10, background: col, boxShadow: GLOW(col, 2), flexShrink: 0 }} />
                <div style={{ flex: 1, height: 10, background: `${col}22` }} />
              </div>
              <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 46, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>{d.value.toLocaleString()}</span>
              {conv && <span style={{ fontFamily: FONT, fontSize: 10, color: C.muted, paddingLeft: 4, flexShrink: 0, width: 34 }}>{conv}</span>}
            </div>
          </div>
        );
      })}
    </Pane>
  );
}

function ChurnViz({ startUsers, churnRate, months }) {
  const data = []; let u = startUsers;
  for (let i = 0; i <= months; i++) { data.push(Math.round(u)); u *= (1 - churnRate); }
  return (
    <Pane title="CHURN DECAY — ORIGINAL 1,000 USERS" status="CRITICAL" accent={C.error}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 10 }}>{">"} COMPOUNDING CHURN @ {(churnRate * 100).toFixed(1)}%/MONTH — NO NEW USERS ADDED</div>
      {data.map((v, i) => {
        const pct = v / startUsers;
        const col = v > startUsers * 0.5 ? C.primary : v > startUsers * 0.25 ? C.secondary : C.error;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 2, overflow: "hidden" }}>
            <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 28, flexShrink: 0 }}>M{String(i).padStart(2, "0")}</span>
            <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
              <div style={{ width: `${pct * 100}%`, height: 9, background: col, boxShadow: GLOW(col, 2), flexShrink: 0 }} />
              <div style={{ flex: 1, height: 9, background: `${col}22` }} />
            </div>
            <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 34, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>{v}</span>
            {i === months && <span style={{ fontFamily: FONT, fontSize: 10, color: C.error, paddingLeft: 4, flexShrink: 1 }}>LOST</span>}
          </div>
        );
      })}
    </Pane>
  );
}

function MrrViz({ data }) {
  const max = Math.max(...data.map(d => d.pro + d.ent));
  return (
    <Pane title="MRR GROWTH — STACKED BY PLAN" status="OK" accent={C.primary}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 10 }}>{">"} MONTHLY RECURRING REVENUE ($)</div>
      {data.map((d, i) => {
        const total = d.pro + d.ent;
        const proPct = d.pro / max;
        const entPct = d.ent / max;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 2, overflow: "hidden" }}>
            <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 24, flexShrink: 0 }}>{d.month}</span>
            <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
              <div style={{ width: `${proPct * 100}%`, height: 9, background: C.primary, boxShadow: GLOW(C.primary, 2), flexShrink: 0 }} />
              <div style={{ width: `${entPct * 100}%`, height: 9, background: C.secondary, boxShadow: GLOW(C.secondary, 2), flexShrink: 0 }} />
              <div style={{ flex: 1, height: 9, background: `${C.primary}22` }} />
            </div>
            <span style={{ fontFamily: FONT, fontSize: 11, color: C.primary, width: 48, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>${(total / 1000).toFixed(1)}K</span>
          </div>
        );
      })}
      <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 8, display: "flex", gap: 16 }}>
        <span><span style={{ color: C.primary }}>██</span> PRO $19/mo</span>
        <span><span style={{ color: C.secondary }}>██</span> ENTERPRISE $79/mo</span>
      </div>
    </Pane>
  );
}

function LtvCacViz({ channels }) {
  const maxVal = Math.max(...channels.flatMap(c => [c.cac, c.ltv]));
  return (
    <Pane title="LTV vs CAC BY CHANNEL" status="WARN" accent={C.secondary}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 10 }}>{">"} GREEN = LTV | RED = CAC | HEALTHY 3:1+</div>
      {channels.map((ch, i) => {
        const ltvPct = ch.ltv / maxVal;
        const cacPct = ch.cac / maxVal;
        const ratio = (ch.ltv / ch.cac).toFixed(1);
        const ok = ch.ltv / ch.cac >= 3, warn = ch.ltv / ch.cac >= 1;
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 2 }}>{ch.name}</div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 2, overflow: "hidden" }}>
              <span style={{ fontFamily: FONT, fontSize: 10, color: C.muted, width: 28, flexShrink: 0 }}>LTV</span>
              <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
                <div style={{ width: `${ltvPct * 100}%`, height: 8, background: C.primary, boxShadow: GLOW(C.primary, 2), flexShrink: 0 }} />
                <div style={{ flex: 1, height: 8, background: `${C.primary}22` }} />
              </div>
              <span style={{ fontFamily: FONT, fontSize: 11, color: C.primary, width: 44, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>${ch.ltv.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
              <span style={{ fontFamily: FONT, fontSize: 10, color: C.muted, width: 28, flexShrink: 0 }}>CAC</span>
              <div style={{ flex: 1, display: "flex", minWidth: 0 }}>
                <div style={{ width: `${cacPct * 100}%`, height: 8, background: C.error, boxShadow: GLOW(C.error, 2), flexShrink: 0 }} />
                <div style={{ flex: 1, height: 8, background: `${C.error}22` }} />
              </div>
              <span style={{ fontFamily: FONT, fontSize: 11, color: C.error, width: 44, textAlign: "right", paddingLeft: 6, flexShrink: 0 }}>${ch.cac.toLocaleString()}</span>
              <span style={{ fontFamily: FONT, fontSize: 11, color: ok ? C.primary : warn ? C.secondary : C.error, fontWeight: "bold", paddingLeft: 4, flexShrink: 0, width: 32 }}>{ratio}:1</span>
            </div>
          </div>
        );
      })}
    </Pane>
  );
}

function DashboardViz({ data }) {
  const d = data;
  const Metric = ({ label, value, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, fontFamily: FONT }}>{">"} {label}</div>
      <div style={{ fontSize: 28, color, textShadow: GLOW(color, 6), fontWeight: "bold", lineHeight: 1.1, fontFamily: FONT }}>{value}</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontFamily: FONT, fontSize: 16, color: C.secondary, textShadow: GLOW(C.secondary, 4), marginBottom: 12, letterSpacing: 1 }}>
        $ present --to=board --mode=confidence
      </div>
      <div className="f-dashboard" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        <Pane title="REVENUE ENGINE" status="OK">
          <Metric label="ARR" value={d.arr} color={C.primary} />
          <Metric label="MRR" value={d.mrr} color={C.primary} />
          <Metric label="NET DOLLAR RETENTION" value={d.ndr} color={C.cyan} />
        </Pane>
        <Pane title="UNIT ECONOMICS" status="OK" accent={C.secondary}>
          <Metric label="LTV" value={d.ltv} color={C.primary} />
          <Metric label="CAC" value={d.cac} color={C.secondary} />
          <Metric label="LTV:CAC RATIO" value={d.ratio} color={C.primary} />
        </Pane>
        <Pane title="GROWTH HEALTH" status="OK" accent={C.cyan}>
          <Metric label="MONTHLY CHURN" value={d.churn} color={C.primary} />
          <Metric label="ACTIVATION RATE" value={d.activation} color={C.cyan} />
          <Metric label="TOTAL CUSTOMERS" value={d.customers.toLocaleString()} color={C.primary} />
          <Metric label="BURN RATE" value={d.burn} color={C.secondary} />
          <Metric label="RUNWAY" value={d.runway} color={C.secondary} />
        </Pane>
      </div>
      <div style={{ fontFamily: FONT, fontSize: 16, color: C.primary, textShadow: GLOW(C.primary, 4), marginTop: 16, padding: "12px 14px", border: `1px dashed ${C.primary}`, lineHeight: 1.7 }}>
        {">"} verdict: Strong retention (3.1% churn), healthy unit economics<br/>
        {">"} (LTV:CAC 4.3:1), and $240K ARR growing ~10% MoM.<br/>
        {">"} This is a machine worth scaling. Series A: READY.
      </div>
    </div>
  );
}

function VizRouter({ viz }) {
  if (!viz) return null;
  const map = { runway: RunwayViz, funnel: FunnelViz, churn: ChurnViz, mrr: MrrViz, ltvcac: LtvCacViz, dashboard: DashboardViz };
  const Comp = map[viz.type];
  return Comp ? <Comp {...viz} /> : null;
}

// ═══════════════════════════════════════════════════
// ─── MAIN APPLICATION ───
// ═══════════════════════════════════════════════════

const PHASES = { INTRO: -1, NARRATIVE: 0, LESSON: 1, QUIZ: 2, RESULT: 3, VIZ: 4 };

export default function FounderSH() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const [chIdx, setChIdx] = useState(0);
  const [phase, setPhase] = useState(PHASES.INTRO);
  const [selOpt, setSelOpt] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [narrDone, setNarrDone] = useState(false);
  const mainRef = useRef(null);

  const ch = CHAPTERS[chIdx];
  const isLast = chIdx === CHAPTERS.length - 1;

  const scroll = () => setTimeout(() => { if (mainRef.current) mainRef.current.scrollTop = mainRef.current.scrollHeight; }, 120);

  const advance = useCallback(() => {
    if (phase === PHASES.INTRO) { setPhase(PHASES.NARRATIVE); setNarrDone(false); scroll(); }
    else if (phase === PHASES.NARRATIVE) { setPhase(PHASES.LESSON); scroll(); }
    else if (phase === PHASES.LESSON) { setPhase(ch.lesson.scenario ? PHASES.QUIZ : PHASES.VIZ); scroll(); }
    else if (phase === PHASES.RESULT) { setPhase(PHASES.VIZ); scroll(); }
    else if (phase === PHASES.VIZ && !isLast) {
      setChIdx(i => i + 1); setPhase(PHASES.INTRO); setSelOpt(null); setNarrDone(false);
      if (mainRef.current) mainRef.current.scrollTop = 0;
    }
  }, [phase, ch, isLast]);

  const handleAnswer = (opt, idx) => {
    setSelOpt(idx); setTotal(t => t + 1);
    if (opt.correct) setScore(s => s + 1);
    setPhase(PHASES.RESULT); scroll();
  };

  const reset = () => { setChIdx(0); setPhase(PHASES.INTRO); setSelOpt(null); setScore(0); setTotal(0); setNarrDone(false); };

  const btnLabels = {
    [PHASES.INTRO]: "[ BEGIN CHAPTER ]",
    [PHASES.NARRATIVE]: "[ CONTINUE → LESSON ]",
    [PHASES.LESSON]: ch.lesson.scenario ? "[ CONTINUE → CHALLENGE ]" : "[ CONTINUE → DATA ]",
    [PHASES.RESULT]: "[ SEE THE DATA ]",
    [PHASES.VIZ]: !isLast ? "[ NEXT CHAPTER → ]" : null,
  };
  const btnText = btnLabels[phase] || null;

  const Btn = ({ onClick, children, color = C.primary }) => (
    <button onClick={onClick} className="f-btn" style={{
      fontFamily: FONT, fontSize: 16, padding: "11px 22px", cursor: "pointer",
      background: "transparent", color, border: `1px solid ${color}`,
      textShadow: GLOW(color, 3), letterSpacing: 2, transition: "all 0.15s",
      display: "block", width: isMobile ? "100%" : "auto",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = C.bg; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = color; }}
    >{children}</button>
  );

  // Determine act color
  const actColor = ch.act === "I" ? C.primary : ch.act === "II" ? C.secondary : C.cyan;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.primary, fontFamily: FONT, display: "flex", flexDirection: "column", overflowX: "hidden", maxWidth: "100vw" }}>
      <Scanlines />

      {/* TOP BAR */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, flexShrink: 0 }}>
        <div style={{ fontSize: 15, letterSpacing: 2 }}>
          <span style={{ color: C.secondary }}>$</span> FOUNDER.SH <span style={{ color: C.muted, fontSize: 10 }}>v2.0 — STARTUP METRICS ACADEMY</span>
        </div>
        <div style={{ fontSize: 13, color: C.muted, display: "flex", gap: 16 }}>
          <span>CH {chIdx + 1}/{CHAPTERS.length}</span>
          <span>SCORE: <span style={{ color: C.primary }}>{score}/{total}</span></span>
        </div>
      </div>

      {/* ACT + CHAPTER NAV */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "6px 16px", display: "flex", gap: 6, overflowX: "auto", flexShrink: 0, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: actColor, fontWeight: "bold", letterSpacing: 2, marginRight: 8, whiteSpace: "nowrap" }}>
          ACT {ch.act}: {ch.actTitle}
        </span>
        {CHAPTERS.map((c, i) => {
          const active = i === chIdx, past = i < chIdx;
          const sameAct = c.act === ch.act;
          return (
            <button key={c.id} onClick={() => { if (i <= chIdx) { setChIdx(i); setPhase(PHASES.INTRO); setSelOpt(null); setNarrDone(false); } }}
              style={{
                fontFamily: FONT, fontSize: 10, padding: "3px 6px",
                cursor: i <= chIdx ? "pointer" : "default",
                background: active ? actColor : "transparent",
                color: active ? C.bg : past ? C.primary : C.muted,
                border: `1px solid ${active ? actColor : C.border}`,
                whiteSpace: "nowrap", opacity: i > chIdx ? 0.3 : sameAct ? 1 : 0.6,
                letterSpacing: 1,
              }}>
              {past ? "✓ " : ""}{c.id.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* MAIN CONTENT */}
      <div ref={mainRef} className="f-main" style={{ flex: 1, overflowY: "auto", padding: "20px 16px", maxWidth: 880, width: "100%", margin: "0 auto" }}>

        {/* CHAPTER HEADER */}
        <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}>
          <div style={{ fontSize: 11, color: actColor, letterSpacing: 2, marginBottom: 4 }}>
            ACT {ch.act} — {ch.actTitle} — CHAPTER {chIdx + 1}
          </div>
          <div style={{ fontSize: 24, color: C.primary, textShadow: GLOW(C.primary, 8), fontWeight: "bold", letterSpacing: 2, textTransform: "uppercase" }}>
            {ch.title}
          </div>
          <div style={{ fontSize: 16, color: C.secondary, textShadow: GLOW(C.secondary, 3), marginTop: 4 }}>
            {ch.subtitle}
          </div>
        </div>

        {/* DRAMATIC INTRO (unique per chapter) */}
        <div style={{ marginBottom: 20, padding: 0, border: `1px solid ${actColor}44`, background: `${actColor}06` }}>
          <div style={{ padding: "4px 12px", background: `${actColor}18`, fontSize: 11, color: actColor, letterSpacing: 2 }}>
            {ch.introLabel || "SITUATION BRIEFING"}
          </div>
          <div style={{ padding: "16px 20px" }}>
            <TypedLines key={`intro-${ch.id}`} lines={ch.terminalIntro} speed={18} lineDelay={50} onDone={() => {}} color={actColor} />
          </div>
        </div>

        {phase === PHASES.INTRO && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}><Btn onClick={advance} color={actColor}>{btnText}</Btn></div>
        )}

        {/* NARRATIVE */}
        {phase >= PHASES.NARRATIVE && (
          <div style={{ marginBottom: 20, padding: "20px 22px", border: `1px solid ${C.border}`, background: C.panel, overflow: "hidden", maxWidth: "100%", boxSizing: "border-box" }}>
            <TypedLines key={`narr-${ch.id}`} lines={ch.narrative} speed={16} lineDelay={70} onDone={() => setNarrDone(true)} />
          </div>
        )}

        {phase === PHASES.NARRATIVE && narrDone && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}><Btn onClick={advance}>{btnText}</Btn></div>
        )}

        {/* LESSON */}
        {phase >= PHASES.LESSON && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}>
            <Pane title={`LESSON: ${ch.lesson.title}`} accent={C.cyan} status="STUDY">
              {/* WHY THIS MATTERS */}
              <div style={{ marginBottom: 16, padding: "10px 12px", border: `1px dashed ${C.cyan}44`, background: `${C.cyan}06` }}>
                <div style={{ fontSize: 11, color: C.cyan, letterSpacing: 2, marginBottom: 4 }}>WHY THIS MATTERS</div>
                <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.7 }}>{ch.lesson.why}</div>
              </div>
              {ch.lesson.metrics.map((m, i) => (
                <div key={i} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: i < ch.lesson.metrics.length - 1 ? `1px dashed ${C.border}` : "none" }}>
                  <div style={{ fontSize: 16, color: C.secondary, fontWeight: "bold", marginBottom: 4, textShadow: GLOW(C.secondary, 3) }}>{m.name}</div>
                  <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.7, marginBottom: 8 }}>{m.def}</div>
                  <div style={{ fontSize: 14, color: C.cyan, padding: "6px 10px", background: `${C.cyan}08`, border: `1px solid ${C.cyan}22` }}>
                    <span style={{ color: C.muted }}>formula:</span> {m.formula}
                  </div>
                </div>
              ))}
              {ch.lesson.metrics.length === 0 && (
                <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.7 }}>
                  You've learned every core metric. Now see them as a single, connected system — the story your startup tells through its numbers.
                </div>
              )}
            </Pane>
            {/* PITFALLS */}
            {ch.pitfalls && ch.pitfalls.length > 0 && (
              <div style={{ marginTop: 12, border: `1px solid ${C.error}33`, background: `${C.error}06` }}>
                <div style={{ padding: "5px 12px", background: `${C.error}18`, fontSize: 11, color: C.error, letterSpacing: 2, display: "flex", justifyContent: "space-between" }}>
                  <span>⚠ COMMON PITFALLS</span>
                  <span style={{ color: C.muted }}>[AVOID]</span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  {ch.pitfalls.map((p, i) => (
                    <div key={i} style={{
                      marginBottom: i < ch.pitfalls.length - 1 ? 12 : 0,
                      paddingBottom: i < ch.pitfalls.length - 1 ? 12 : 0,
                      borderBottom: i < ch.pitfalls.length - 1 ? `1px dashed ${C.error}22` : "none",
                    }}>
                      <div style={{ fontSize: 14, color: C.error, lineHeight: 1.7, marginBottom: 4 }}>
                        <span style={{ fontWeight: "bold", marginRight: 6 }}>[{String(i + 1).padStart(2, "0")}]</span>
                        {p.mistake}
                      </div>
                      <div style={{ fontSize: 14, color: C.primary, lineHeight: 1.7, paddingLeft: 32 }}>
                        <span style={{ color: C.cyan, marginRight: 6 }}>→</span>{p.fix}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {phase === PHASES.LESSON && (
              <div style={{ marginTop: 16 }}><Btn onClick={advance}>{btnText}</Btn></div>
            )}
          </div>
        )}

        {/* QUIZ */}
        {phase >= PHASES.QUIZ && ch.lesson.scenario && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}>
            <Pane title="CHALLENGE" accent={C.secondary} status="ACTIVE">
              <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.7, marginBottom: 14 }}>{ch.lesson.scenario.intro}</div>
              <div style={{ fontSize: 16, color: C.secondary, fontWeight: "bold", marginBottom: 14, padding: "10px 12px", border: `1px dashed ${C.secondary}`, textShadow: GLOW(C.secondary, 3) }}>
                <span style={{ color: C.muted }}>{">"}</span> {ch.lesson.scenario.question}
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {ch.lesson.scenario.options.map((opt, i) => {
                  const chosen = selOpt === i, revealed = phase >= PHASES.RESULT, correct = opt.correct;
                  let bg = "transparent", bc = C.border, tc = C.primary;
                  if (revealed && chosen && correct) { bg = `${C.primary}15`; bc = C.primary; }
                  else if (revealed && chosen && !correct) { bg = `${C.error}15`; bc = C.error; tc = C.error; }
                  else if (revealed && correct) { bg = `${C.primary}08`; bc = C.primary; }
                  return (
                    <button key={i} onClick={() => phase === PHASES.QUIZ && handleAnswer(opt, i)} disabled={phase !== PHASES.QUIZ}
                      className="f-option"
                      style={{
                        fontFamily: FONT, fontSize: 15, textAlign: "left", padding: "10px 14px",
                        background: bg, color: tc, border: `1px solid ${bc}`,
                        cursor: phase === PHASES.QUIZ ? "pointer" : "default",
                        textShadow: chosen ? GLOW(tc, 3) : "none", transition: "all 0.15s", lineHeight: 1.5,
                      }}
                      onMouseEnter={e => { if (phase === PHASES.QUIZ) { e.target.style.borderColor = C.primary; e.target.style.textShadow = GLOW(C.primary, 3); } }}
                      onMouseLeave={e => { if (phase === PHASES.QUIZ) { e.target.style.borderColor = C.border; e.target.style.textShadow = "none"; } }}
                    ><span style={{ color: C.muted }}>[{String.fromCharCode(65 + i)}]</span> {opt.label}</button>
                  );
                })}
              </div>
            </Pane>
          </div>
        )}

        {/* FEEDBACK */}
        {phase >= PHASES.RESULT && ch.lesson.scenario && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}>
            <Pane title={ch.lesson.scenario.options[selOpt]?.correct ? "CORRECT" : "INCORRECT"}
              accent={ch.lesson.scenario.options[selOpt]?.correct ? C.primary : C.error}
              status={ch.lesson.scenario.options[selOpt]?.correct ? "OK" : "ERR"}>
              <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.7 }}>{ch.lesson.scenario.options[selOpt]?.feedback}</div>
            </Pane>
            {phase === PHASES.RESULT && (
              <div style={{ marginTop: 16 }}><Btn onClick={advance}>{btnText}</Btn></div>
            )}
          </div>
        )}

        {/* VIZ */}
        {phase >= PHASES.VIZ && (
          <div style={{ marginBottom: 20, maxWidth: "100%", overflow: "hidden" }}>
            <VizRouter viz={ch.lesson.viz} />

            {/* COMMON PITFALLS */}
            {ch.pitfalls && ch.pitfalls.length > 0 && (
              <div style={{
                marginTop: 16, border: `1px solid ${C.error}44`, background: `${C.error}06`,
              }}>
                <div style={{
                  padding: "5px 12px", background: `${C.error}18`, fontSize: 11,
                  color: C.error, letterSpacing: 2, fontFamily: FONT,
                  display: "flex", justifyContent: "space-between",
                }}>
                  <span>COMMON PITFALLS</span>
                  <span style={{ opacity: 0.6 }}>AVOID THESE</span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  {ch.pitfalls.map((p, i) => (
                    <div key={i} style={{
                      marginBottom: i < ch.pitfalls.length - 1 ? 14 : 0,
                      paddingBottom: i < ch.pitfalls.length - 1 ? 14 : 0,
                      borderBottom: i < ch.pitfalls.length - 1 ? `1px dashed ${C.error}33` : "none",
                    }}>
                      <div style={{ fontSize: 14, color: C.error, fontFamily: FONT, marginBottom: 4, lineHeight: 1.6 }}>
                        <span style={{ color: C.muted }}>✗</span> {p.mistake}
                      </div>
                      <div style={{ fontSize: 14, color: C.primary, fontFamily: FONT, lineHeight: 1.6, paddingLeft: 14 }}>
                        <span style={{ color: C.primary, opacity: 0.5 }}>→</span> {p.fix}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* KEY TAKEAWAY ALERT */}
            {ch.takeaway && (
              <div style={{
                marginTop: 16, padding: "12px 14px",
                border: `1px solid ${C.secondary}`,
                background: `${C.secondary}0a`,
                display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <div style={{
                  fontFamily: FONT, fontSize: 13, color: C.bg, background: C.secondary,
                  padding: "2px 8px", fontWeight: "bold", letterSpacing: 1, flexShrink: 0,
                  textShadow: "none",
                }}>KEY TAKEAWAY</div>
                <div style={{
                  fontFamily: FONT, fontSize: 16, color: C.secondary,
                  textShadow: GLOW(C.secondary, 3), lineHeight: 1.7,
                }}>{ch.takeaway}</div>
              </div>
            )}
            {btnText && <div style={{ marginTop: 16 }}><Btn onClick={advance} color={C.secondary}>{btnText}</Btn></div>}
            {isLast && phase === PHASES.VIZ && (
              <div style={{ marginTop: 24, textAlign: "center" }}>
                <div style={{ fontSize: 16, color: C.cyan, textShadow: GLOW(C.cyan, 8), marginBottom: 8, letterSpacing: 2 }}>
                  ═══════ COURSE COMPLETE ═══════
                </div>
                <div style={{ fontSize: 14, color: C.secondary, marginBottom: 6 }}>
                  FINAL SCORE: {score}/{total}
                </div>
                <div style={{ fontSize: 14, color: C.primary, marginBottom: 16, lineHeight: 1.7, maxWidth: "100%", margin: "0 auto 16px" }}>
                  You've learned the metrics that matter. But remember: numbers are the language — the STORY is what you're building. Go build something worth measuring.
                </div>
                <button onClick={reset} style={{
                  fontFamily: FONT, fontSize: 15, padding: "10px 24px", cursor: "pointer",
                  background: C.cyan, color: C.bg, border: `1px solid ${C.cyan}`,
                  letterSpacing: 2, fontWeight: "bold",
                }}
                  onMouseEnter={e => { e.target.style.background = C.primary; e.target.style.borderColor = C.primary; }}
                  onMouseLeave={e => { e.target.style.background = C.cyan; e.target.style.borderColor = C.cyan; }}
                >[ RESTART FROM ACT I ]</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: "6px 16px", fontSize: 11, color: C.muted, display: "flex", justifyContent: "space-between", flexShrink: 0, flexWrap: "wrap", gap: 4 }}>
        <span><span style={{ color: C.secondary }}>$</span> founder.sh --mode=academy --v2.0</span>
        <span>numbers tell stories. build yours.</span>
      </div>
    </div>
  );
}
